import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  /** Field label rendered above the input. */
  label?: string;
  /** Inline error message; also turns the border red when set. */
  error?: string | null;
  /** Value-only change handler (no event plumbing at call sites). */
  onChange?: (value: string) => void;
  /** Slot pinned to the right inside the input — e.g. a loading spinner. */
  rightSlot?: ReactNode;
  /** Extra classes on the outer <label> wrapper. */
  containerClassName?: string;
}

/**
 * Design-system text input — light surface by default (dark text on a
 * light fill) so it reads on the white address/modal cards. Every colour
 * flows through `--co-TextField-*` knobs with the shared Figma tokens as
 * fallbacks, so a partner can retheme inputs app-wide. There was no form
 * primitive in the DS before; this is it.
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    { label, error, onChange, rightSlot, containerClassName = '', className = '', style, ...rest },
    ref,
  ) {
    return (
      <label
        data-co-component="TextField"
        className={`flex flex-col gap-1.5 ${containerClassName}`}
      >
        {label && (
          <span
            className="text-[13px] leading-[16px]"
            style={{ color: 'var(--co-TextField-labelColor, #6B7280)' }}
          >
            {label}
          </span>
        )}
        <span className="relative flex items-center">
          <input
            ref={ref}
            onChange={(e) => onChange?.(e.target.value)}
            className={`
              h-12 w-full rounded-[12px] px-3.5 text-[15px] outline-none
              transition-shadow placeholder:opacity-50
              focus:[box-shadow:0_0_0_2px_var(--co-TextField-focusRing,rgba(34,225,28,0.35))]
              ${rightSlot ? 'pr-10' : ''}
              ${className}
            `}
            style={{
              backgroundColor: 'var(--co-TextField-fill, #F2F2F2)',
              border: `1px solid ${
                error ? '#E5484D' : 'var(--co-TextField-border, #E2E2E5)'
              }`,
              color: 'var(--co-TextField-textColor, #15181E)',
              ...style,
            }}
            {...rest}
          />
          {rightSlot && <span className="absolute right-3 flex items-center">{rightSlot}</span>}
        </span>
        {error && (
          <span className="text-[12px]" style={{ color: '#E5484D' }}>
            {error}
          </span>
        )}
      </label>
    );
  },
);
